import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import {
  ImageCroppedEvent,
  ImageCropperComponent,
  ImageTransform,
  ImageCropperModule,
} from 'ngx-image-cropper';
import { Capacitor } from '@capacitor/core';
import { addIcons } from 'ionicons';
import { Subscription } from 'rxjs';
import {
  addCircle,
  refresh,
  codeOutline,
  chevronExpandOutline,
  closeCircleOutline,
  checkmarkCircleOutline,
} from 'ionicons/icons';
import {
  IonIcon,
  IonModal,
  IonRow,
  IonCol,
  IonButton,
  IonPopover,
  IonImg,
  IonSkeletonText,
} from '@ionic/angular/standalone';
import { BusinessService } from '../../../core/services/business/business.service';
import { AlertService } from '../../../core/services/navigation/alert.service';
import { LoggerService } from '../../../core/services/logger/logger.service';
import { downscaleImageFile, toJpg } from '../../../core/utilities/image-resize.util';
import { CommonModule } from '@angular/common';
import { Business } from 'src/app/core/models/Business';

@Component({
  selector: 'app-business-avatar',
  templateUrl: './business-avatar.component.html',
  styleUrls: ['./business-avatar.component.scss'],
  standalone: true,
  imports: [ImageCropperModule, IonIcon, IonModal, IonRow, IonCol, IonButton, IonPopover, IonImg, IonSkeletonText, CommonModule],
})
export class BusinessAvatarComponent implements OnInit, OnDestroy {
  @ViewChild('cropper') cropper!: ImageCropperComponent;
  @Input() set avatarUrl(url: string | null) {
    if (url) {
      this.downloadImage();
    }
  }
  @Output() upload = new EventEmitter<File>();

  _avatarUrl!: string;
  imageFile?: File;
  transform: ImageTransform = {};
  maintainAspectRatio: boolean = true;
  isMobile = Capacitor.getPlatform() !== 'web';
  isModalOpen = false;
  businessId!: string;
  business!: Business;
  isImageLoaded: boolean = false;
  private readonly subscriptions = new Subscription();
  private currentObjectUrl: string | null = null;

  constructor(
    private businessService: BusinessService,
    private readonly alertService: AlertService,
    private readonly logger: LoggerService,
  ) {
    addIcons({
      addCircle,
      refresh,
      codeOutline,
      chevronExpandOutline,
      closeCircleOutline,
      checkmarkCircleOutline,
    });
  }

  ngOnInit(): void {
    if (this.avatarUrl) {
      this._avatarUrl = this.avatarUrl;
    }
    this.getBusinessInfo();
  }

  getBusinessInfo() {
    this.subscriptions.add(
      this.businessService.business$.subscribe(business => {
        if (business) {
          this.businessId = String(business.id);
          this.business = business;
        }
      }),
    );
  }

  downloadImage() {
    // Download image from server
  }

  async selectImage(e: Event): Promise<void> {
    const input: HTMLInputElement = e.target as HTMLInputElement;
    const file: File | undefined = input.files?.[0];
    if (!file) {
      return;
    }
    this.maintainAspectRatio = true;
    try {
      this.imageFile = await downscaleImageFile(file);
      this.setOpen(true);
    } catch (error: unknown) {
      this.logger.error('Failed to prepare selected business image', error, { fileSize: file.size });
      this.alertService.showAlertMessage('Upload failed', 'We could not open that image. Please try a different photo.');
    } finally {
      input.value = '';
    }
  }

  setOpen(isOpen: boolean) {
    this.isModalOpen = isOpen;
  }

  imageCropped(event: ImageCroppedEvent) {
    // Revoke previous object URL to prevent memory leak
    if (this.currentObjectUrl) {
      URL.revokeObjectURL(this.currentObjectUrl);
    }
    
    this._avatarUrl = URL.createObjectURL(event.blob!);
    this.currentObjectUrl = this._avatarUrl;

    toJpg(this._avatarUrl, { name: 'avatar.jpg' }).then((file: File) => {
      this.upload.emit(file);
    }).catch((error: unknown) => {
      this.logger.error('Failed to convert business image', error);
      this.alertService.showAlertMessage('Upload failed', 'We could not process that image. Please try a different photo.');
    });
    this.setOpen(false);
  }

  imageLoaded(): void {
    setTimeout(() => {
      this.maintainAspectRatio = false;
    }, 500);
  }

  loadImageFailed() {
    this.setOpen(false);
    this.logger.error('Cropper failed to load business image');
    this.alertService.showAlertMessage('Upload failed', 'We could not open that image. Please try a different photo.');
  }

  ngOnDestroy(): void {
    // Clean up object URL on component destruction
    if (this.currentObjectUrl) {
      URL.revokeObjectURL(this.currentObjectUrl);
    }
    this.subscriptions.unsubscribe();
  }

  cropImage() {
    this.cropper.crop();
    this.setOpen(false);
  }

  discardChanges() {
    this.setOpen(false);
  }

  // Image edit functions
  rotate() {
    const newValue: number = ((this.transform.rotate ?? 0) + 90) % 360;

    this.transform = {
      ...this.transform,
      rotate: newValue,
    };
  }

  flipHorizontal() {
    this.transform = {
      ...this.transform,
      flipH: !this.transform.flipH,
    };
  }

  flipVertical() {
    this.transform = {
      ...this.transform,
      flipV: !this.transform.flipV,
    };
  }

}
